'use client';

import type { ExpandedState, SortingState, TableState } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { Fragment, type ReactNode, useState } from 'react';
import { cn } from '@/utils/сn';
import { NoSearchYetState } from '../EmptyState/NoSearchYetState';
import { NothingFoundState } from '../EmptyState/NothingFoundState';
import { Popup } from '../Popup';
import { Spinner } from '../Spinner';
import { FilterItem } from './components/FilterItem';
import { SortTriangleIndicator } from './components/SortTriangleIndicator';
import { useSorting } from './hooks/useSorting';
import styles from './styles.module.css';
import type { TableColumn } from './types';
import FilterFunnelIcon from '/public/icons/filter-funnel.svg';
import SortIcon from '/public/icons/sort.svg';

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  className?: string;
  sortIconType?: 'default' | 'triangle';
  state?: Partial<TableState>;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  search?: string;
  onColumnSort?: (columnId: string) => void;
  onSortingChange?: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  /** Server already sorted `data`. Skips the client re-sort of the current page. */
  manualSorting?: boolean;
  getRowCanExpand?: (row: T) => boolean;
  renderExpandedRow?: (row: T) => ReactNode;
  fluidOnMobile?: boolean;
  /**
   * Classes for the bordered scroll container around the table.
   *
   * @remarks Its CSS-module styles sit outside Tailwind's layers, so an override needs the
   * important modifier (`!rounded-xl`).
   */
  containerClassName?: string;
}

export const Table = <T,>({
  columns,
  data,
  state,
  className,
  onRowClick,
  isLoading,
  sortIconType = 'default',
  search,
  onColumnSort,
  onSortingChange: onSortingChangeProp,
  manualSorting = false,
  getRowCanExpand,
  renderExpandedRow,
  fluidOnMobile = false,
  containerClassName,
}: TableProps<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const expandStateEnabled = Boolean(getRowCanExpand);
  const { onSortingChange: onUrlSortingChange } = useSorting();
  const onSortingChange = onSortingChangeProp
    ? onSortingChangeProp
    : onColumnSort
      ? (updater: SortingState | ((prev: SortingState) => SortingState)) => {
          const next = typeof updater === 'function' ? updater([]) : updater;
          if (next.length > 0) onColumnSort(next[0].id);
        }
      : undefined;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
    enableSorting: true,
    getRowCanExpand: getRowCanExpand ? (row) => getRowCanExpand(row.original) : undefined,
    onExpandedChange: expandStateEnabled ? setExpanded : undefined,
    state: {
      ...state,
      ...(expandStateEnabled ? { expanded } : {}),
    },
    onSortingChange,
  });
  const leafColumns = table.getVisibleLeafColumns();

  return (
    <div className={cn(styles.tableContainer, containerClassName)}>
      <table
        className={cn(styles.table, fluidOnMobile && styles.fluidOnMobile, className)}
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          {leafColumns.map((col) => (
            <col
              key={col.id}
              style={(col.columnDef as TableColumn<T>).flex ? undefined : { width: `${col.getSize()}px` }}
            />
          ))}
        </colgroup>
        <thead className={styles.tableHeader}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const headerLabel =
                  typeof header.column.columnDef.header === 'string'
                    ? header.column.columnDef.header
                    : header.column.id;

                return (
                  <th
                    key={header.id}
                    aria-sort={
                      header.column.getCanSort()
                        ? sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : 'none'
                        : undefined
                    }
                  >
                    <div className={styles.headerCell}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'select-none flex gap-3 items-center rounded-md',
                            (header.column.columnDef as TableColumn<T>)?.align ?? 'justify-start'
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() ? (
                            <button
                              type="button"
                              disabled={isLoading || !data.length}
                              className={cn('leading-none', {
                                'cursor-not-allowed': isLoading || !data.length,
                                'cursor-pointer': !isLoading && data.length,
                              })}
                              onClick={() =>
                                onSortingChange
                                  ? header.column.getToggleSortingHandler()?.({} as unknown as MouseEvent)
                                  : onUrlSortingChange(header.column.id)
                              }
                            >
                              {sortIconType === 'default' ? <SortIcon /> : <SortTriangleIndicator direction={sorted} />}
                              <span className="sr-only">
                                {sorted
                                  ? `${headerLabel}, sorted ${sorted === 'asc' ? 'ascending' : 'descending'}`
                                  : `Sort by ${headerLabel}`}
                              </span>
                            </button>
                          ) : null}
                          {(header.column.columnDef as TableColumn<T>)?.filterOptions ? (
                            <div className="max-h-[200px] overflow-y-auto" key={header.column.id}>
                              <Popup
                                trigger={
                                  <FilterFunnelIcon
                                    className={cn('cursor-pointer', {
                                      'fill-current text-blue-300': (header.column.columnDef as TableColumn<T>)
                                        ?.isFilterActive,
                                      'fill-none': !(header.column.columnDef as TableColumn<T>)?.isFilterActive,
                                    })}
                                  />
                                }
                              >
                                {() => (
                                  <div className="w-[250px] bg-white rounded-lg shadow-sm max-h-[200px] overflow-y-auto">
                                    <ul className="w-full h-full rounded-lg shadow-lg flex flex-col items-start justify-start">
                                      {(header.column.columnDef as TableColumn<T>)?.filterOptions?.map((filter) => (
                                        <FilterItem {...filter} key={filter.id} />
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </Popup>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className={styles.tableBody}>
          {table.getRowModel().rows.map((row) => {
            const showExpandedRow = Boolean(renderExpandedRow && row.getIsExpanded());
            return (
              <Fragment key={row.id}>
                <tr
                  className={cn(styles.tableRow, {
                    [styles.clickableRow]: onRowClick,
                  })}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const colDef = cell.column.columnDef as TableColumn<T>;
                    const autoCellHeight = colDef?.tableCellAutoHeight;
                    const rowH = colDef?.rowHeight || 72;
                    return (
                      <td key={cell.id}>
                        <div
                          className={cn(
                            styles.tableCell,
                            autoCellHeight && styles.tableCellAutoHeight,
                            colDef?.align,
                            colDef?.cellClassName
                          )}
                          style={
                            autoCellHeight ? { minHeight: rowH, height: 'auto' } : { minHeight: rowH, height: '100%' }
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {showExpandedRow && renderExpandedRow ? (
                  <tr className={styles.expandedRow}>
                    <td className={styles.expandedRowCell} colSpan={row.getVisibleCells().length}>
                      {renderExpandedRow(row.original)}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center mt-8 min-h-[380px]">
          <Spinner />
        </div>
      ) : null}
      {!search?.length && !isLoading && !data.length ? (
        <div className="flex flex-1 items-center justify-center mt-[100px] pb-[100px]">
          <NoSearchYetState />
        </div>
      ) : null}

      {(search?.length ?? 0) > 0 && !isLoading && !data.length ? (
        <div className="flex flex-1 items-center justify-center mt-[100px] pb-[100px]">
          <NothingFoundState />
        </div>
      ) : null}
    </div>
  );
};
